import type { DailyCall } from '@daily-co/daily-js';

export type SessionCallJoinErrorKind =
	| 'not-yet-available'
	| 'room-not-ready'
	| 'access-denied'
	| 'not-allowed'
	| 'connection'
	| 'expired'
	| 'generic';

export const SESSION_CALL_RETRYABLE_ERROR_KINDS = [
	'not-yet-available',
	'room-not-ready',
] as const satisfies readonly SessionCallJoinErrorKind[];

export function isSessionCallRetryableErrorKind(
	kind: SessionCallJoinErrorKind | null,
): kind is (typeof SESSION_CALL_RETRYABLE_ERROR_KINDS)[number] {
	return (
		kind !== null &&
		(SESSION_CALL_RETRYABLE_ERROR_KINDS as readonly string[]).includes(kind)
	);
}

export const SESSION_CALL_DURATION_MS = 50 * 60_000;
export const SESSION_CALL_ENTRY_GRACE_MS = 10 * 60_000;

export const SESSION_CALL_EXPIRED_MESSAGE =
	'Esta reunião não está mais disponível. O horário da sessão já passou ou a sala foi encerrada.';

export function getSessionCallEndMs(
	selectedDate: Date | number | string | null | undefined,
): number | null {
	const startMs = toTimestampMs(selectedDate);
	if (startMs === null) {
		return null;
	}

	return startMs + SESSION_CALL_DURATION_MS + SESSION_CALL_ENTRY_GRACE_MS;
}

export function isSessionCallPeriodElapsed(
	session: Pick<Sessions.Session, 'selected_date' | 'status'>,
	now = Date.now(),
): boolean {
	if (session.status === 'FINISHED' || session.status === 'CANCELLED') {
		return true;
	}

	const endMs = getSessionCallEndMs(session.selected_date);
	return endMs !== null && now > endMs;
}

export function toTimestampMs(
	value: Date | number | string | null | undefined,
): number | null {
	if (!value) {
		return null;
	}

	if (value instanceof Date) {
		const dateMs = value.getTime();
		return Number.isNaN(dateMs) ? null : dateMs;
	}

	if (typeof value === 'number') {
		const timestampMs = value > 1_000_000_000_000 ? value : value * 1000;
		return Number.isNaN(timestampMs) ? null : timestampMs;
	}

	const parsedMs = new Date(value).getTime();
	return Number.isNaN(parsedMs) ? null : parsedMs;
}

export function getLocalParticipantEjectAtMs(call: DailyCall): number | null {
	try {
		return toTimestampMs(call.participants().local?.will_eject_at);
	} catch {
		return null;
	}
}

export async function resolveMeetingEndMs(
	call: DailyCall,
): Promise<number | null> {
	const localEjectAtMs = getLocalParticipantEjectAtMs(call);
	if (localEjectAtMs !== null) {
		return localEjectAtMs;
	}

	try {
		const room = await call.room();
		if (!room || !('tokenConfig' in room) || !('config' in room)) {
			return null;
		}

		return (
			toTimestampMs(room.tokenConfig?.exp) ??
			toTimestampMs(room.config?.exp) ??
			null
		);
	} catch {
		return null;
	}
}

export function formatTimeUntil(ms: number | null): string | null {
	if (ms === null || Number.isNaN(ms)) {
		return null;
	}

	if (ms <= 0) {
		return 'alguns instantes';
	}

	const totalSeconds = Math.ceil(ms / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	if (hours > 0) {
		return `${hours}h ${minutes}min`;
	}

	if (minutes > 0) {
		return `${minutes}min ${seconds}s`;
	}

	return `${seconds}s`;
}

export function extractDailyJoinErrorDetails(
	error: unknown,
	fallbackMessage?: string | null,
): { kind: SessionCallJoinErrorKind; message: string } {
	const dailyError = error as
		| {
				errorMsg?: string;
				error?: { msg?: string; type?: string };
				type?: string;
				msg?: string;
		  }
		| undefined;

	const errorType = dailyError?.error?.type ?? dailyError?.type ?? null;
	const rawMessage =
		dailyError?.errorMsg ??
		dailyError?.error?.msg ??
		dailyError?.msg ??
		fallbackMessage ??
		null;

	if (errorType === 'nbf-room' || errorType === 'nbf-token') {
		return {
			kind: 'not-yet-available',
			message: 'A reunião ainda não está disponível para entrada.',
		};
	}

	if (errorType === 'not-allowed') {
		return {
			kind: 'not-allowed',
			message:
				'Não foi permitido entrar na reunião com as configurações atuais.',
		};
	}

	if (errorType === 'connection-error') {
		return {
			kind: 'connection',
			message:
				'Não foi possível conectar à reunião. Verifique sua conexão e tente novamente.',
		};
	}

	if (errorType === 'exp-room' || errorType === 'exp-token') {
		return {
			kind: 'expired',
			message: SESSION_CALL_EXPIRED_MESSAGE,
		};
	}

	if (
		typeof rawMessage === 'string' &&
		rawMessage.toLowerCase().includes('this room is no longer available')
	) {
		return {
			kind: 'expired',
			message: SESSION_CALL_EXPIRED_MESSAGE,
		};
	}

	if (typeof rawMessage === 'string' && rawMessage.trim()) {
		return { kind: 'generic', message: rawMessage };
	}

	return {
		kind: 'generic',
		message: 'Não foi possível iniciar a chamada. Tente novamente.',
	};
}

export function resolveSessionCallJoinError(
	error: unknown,
	fallbackMessage?: string | null,
	options?: { sessionStartMs?: number | null; now?: number },
): { kind: SessionCallJoinErrorKind; message: string } {
	const details = extractDailyJoinErrorDetails(error, fallbackMessage);
	const now = options?.now ?? Date.now();
	const sessionStartMs = options?.sessionStartMs ?? null;

	if (
		details.kind === 'not-yet-available' &&
		sessionStartMs !== null &&
		sessionStartMs <= now
	) {
		return { kind: 'expired', message: SESSION_CALL_EXPIRED_MESSAGE };
	}

	if (details.kind === 'expired') {
		return { kind: 'expired', message: SESSION_CALL_EXPIRED_MESSAGE };
	}

	return details;
}

export function shouldHandleCallJoinError(call: DailyCall): boolean {
	try {
		const state = call.meetingState();
		return state !== 'joined-meeting' && state !== 'left-meeting';
	} catch {
		return true;
	}
}
